import { useState } from "react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar, Clock, MapPin, User, Plus, Calendar as CalendarIcon } from "lucide-react";
import { format, parseISO, isAfter, isBefore } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";

interface Appointment {
  id: string;
  patient_id: string;
  doctor_id?: string;
  doctor_name?: string;
  start_time: string;
  end_time: string;
  status: string;
  notes?: string;
  location?: string;
  appointment_type?: string;
  created_at: string;
}

interface AppointmentsCardProps {
  appointments: Appointment[];
  patientId?: string;
}

export function AppointmentsCard({ appointments, patientId }: AppointmentsCardProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const { profile } = useAuth();
  const queryClient = useQueryClient();

  // Sort appointments by date (newest first)
  const sortedAppointments = [...(appointments || [])].sort(
    (a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
  );

  // Separate upcoming and past appointments
  const now = new Date();
  const upcomingAppointments = sortedAppointments.filter(
    (appointment) => isAfter(parseISO(appointment.start_time), now)
  );
  const pastAppointments = sortedAppointments.filter(
    (appointment) => isBefore(parseISO(appointment.start_time), now)
  );

  const handleViewDetails = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setIsDialogOpen(true);
  };

  const formatDate = (dateString: string) => {
    try {
      return format(parseISO(dateString), "MMM d, yyyy");
    } catch (e) {
      return dateString;
    }
  };

  const formatTime = (dateString: string) => {
    try {
      return format(parseISO(dateString), "h:mm a");
    } catch (e) {
      return dateString;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case 'scheduled':
        return <Badge className="bg-blue-500">Scheduled</Badge>;
      case 'completed':
        return <Badge className="bg-green-500">Completed</Badge>;
      case 'cancelled':
        return <Badge className="bg-red-500">Cancelled</Badge>;
      case 'no-show':
        return <Badge className="bg-amber-500">No Show</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div>
            <CardTitle>Appointments</CardTitle>
            <CardDescription>Patient appointment history</CardDescription>
          </div>
          {patientId && profile?.id && (
            <Button 
              size="sm" 
              className="ml-auto"
              onClick={() => {
                // This would typically open a scheduling dialog
                // For now, we'll just log that it was clicked
                console.log("Schedule appointment clicked for patient:", patientId);
              }}
            >
              <Plus className="w-4 h-4 mr-1" />
              Schedule
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {sortedAppointments.length > 0 ? (
            <>
              {upcomingAppointments.length > 0 && (
                <div>
                  <h3 className="font-medium text-sm mb-2">Upcoming Appointments</h3>
                  <div className="space-y-2">
                    {upcomingAppointments.map((appointment) => (
                      <div 
                        key={appointment.id} 
                        className="flex items-center justify-between p-3 border rounded-md hover:bg-gray-50 cursor-pointer"
                        onClick={() => handleViewDetails(appointment)}
                      >
                        <div className="flex items-center space-x-3">
                          <div className="p-2 bg-blue-100 rounded-full">
                            <Calendar className="w-4 h-4 text-blue-500" />
                          </div>
                          <div>
                            <p className="font-medium text-sm">{formatDate(appointment.start_time)}</p>
                            <p className="text-xs text-gray-500">{formatTime(appointment.start_time)} - {formatTime(appointment.end_time)}</p>
                          </div>
                        </div>
                        {getStatusBadge(appointment.status)}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {pastAppointments.length > 0 && (
                <div>
                  <h3 className="font-medium text-sm mb-2">Past Appointments</h3>
                  <div className="space-y-2">
                    {pastAppointments.slice(0, 5).map((appointment) => (
                      <div 
                        key={appointment.id} 
                        className="flex items-center justify-between p-3 border rounded-md hover:bg-gray-50 cursor-pointer"
                        onClick={() => handleViewDetails(appointment)}
                      >
                        <div className="flex items-center space-x-3">
                          <div className="p-2 bg-gray-100 rounded-full">
                            <Calendar className="w-4 h-4 text-gray-500" />
                          </div>
                          <div>
                            <p className="font-medium text-sm">{formatDate(appointment.start_time)}</p>
                            <p className="text-xs text-gray-500">{formatTime(appointment.start_time)} - {formatTime(appointment.end_time)}</p>
                          </div>
                        </div>
                        {getStatusBadge(appointment.status)}
                      </div>
                    ))}
                    {pastAppointments.length > 5 && (
                      <Button variant="ghost" size="sm" className="w-full text-gray-500">
                        View {pastAppointments.length - 5} more past appointments
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-6">
              <CalendarIcon className="w-12 h-12 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-600">No appointments available</p>
              {patientId && profile?.id && (
                <Button variant="outline" className="mt-4">
                  <Plus className="w-4 h-4 mr-1" />
                  Schedule First Appointment
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Appointment Details Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Appointment Details</DialogTitle>
            <DialogDescription>
              Appointment information and details
            </DialogDescription>
          </DialogHeader>
          
          {selectedAppointment && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-500">Date</p>
                  <p className="text-sm">{formatDate(selectedAppointment.start_time)}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Time</p>
                  <p className="text-sm">{formatTime(selectedAppointment.start_time)} - {formatTime(selectedAppointment.end_time)}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Status</p>
                  <div className="mt-1">{getStatusBadge(selectedAppointment.status)}</div>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500">Type</p>
                  <p className="text-sm">{selectedAppointment.appointment_type || "General"}</p>
                </div>
              </div>
              
              {selectedAppointment.doctor_name && (
                <div>
                  <p className="text-sm font-medium text-gray-500">Doctor</p>
                  <div className="flex items-center mt-1">
                    <User className="w-4 h-4 mr-2 text-gray-500" />
                    <p className="text-sm">{selectedAppointment.doctor_name}</p>
                  </div>
                </div>
              )}
              
              {selectedAppointment.location && (
                <div>
                  <p className="text-sm font-medium text-gray-500">Location</p>
                  <div className="flex items-center mt-1">
                    <MapPin className="w-4 h-4 mr-2 text-gray-500" />
                    <p className="text-sm">{selectedAppointment.location}</p>
                  </div>
                </div>
              )}
              
              {selectedAppointment.notes && (
                <div>
                  <p className="text-sm font-medium text-gray-500">Notes</p>
                  <p className="text-sm mt-1 p-2 bg-gray-50 rounded-md">{selectedAppointment.notes}</p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="sm:justify-between">
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Close
            </Button>
            {selectedAppointment && isAfter(parseISO(selectedAppointment.start_time), now) && (
              <Button variant="destructive">
                Cancel Appointment
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
